import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "@/components/SubscriptionContext";
import { Button } from "@/components/ui/button";
import { Crown, ArrowLeft, Eye } from "lucide-react";
import Layout from "@/components/Layout";

const FEATURE_LABELS = {
  crm: { name: "CRM & Customer History", tier: "Growth" },
  crew_messaging: { name: "Team Messaging", tier: "Growth" },
  quotes_contracts: { name: "Quotes & Contracts", tier: "Growth" },
  pricebook_inventory: { name: "Pricebook & Inventory", tier: "Pro" },
  ai_foreman: { name: "TradeIQ AI Assistant", tier: "Pro" },
  reports_export: { name: "Reports & Export", tier: "Pro" },
  service_agreements: { name: "Service Agreements", tier: "Elite" },
  customer_portal: { name: "Customer Portal", tier: "Elite" },
  custom_branding: { name: "Custom Branding", tier: "Elite" },
};

export default function FeatureGate({ feature, children }) {
  const { hasFeature, loading } = useSubscription();
  const navigate = useNavigate();
  const [previewing, setPreviewing] = useState(false);

  if (loading) return null;
  if (hasFeature(feature)) return children;

  const info = FEATURE_LABELS[feature] || { name: feature, tier: "a higher" };

  // Preview mode — show children with overlay
  if (previewing) {
    return (
      <>
        <div className="relative">
          <div className="pointer-events-none select-none opacity-60 blur-[1px]">
            {children}
          </div>
          <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-amber-600 text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-3" data-testid="preview-banner">
            <Eye className="h-4 w-4" />
            <span className="font-medium text-sm">Preview Mode — {info.name}</span>
            <Button size="sm" variant="secondary" className="h-7 text-xs" onClick={() => navigate("/settings?tab=billing")}>
              Upgrade to {info.tier}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs text-white hover:text-white/80" onClick={() => setPreviewing(false)}>
              Exit Preview
            </Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <Layout>
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="max-w-md w-full text-center px-6">
          <div className="inline-flex items-center justify-center h-20 w-20 bg-amber-100 dark:bg-amber-500/15 rounded-2xl mb-6">
            <Crown className="h-10 w-10 text-amber-600" />
          </div>
          <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "Manrope, sans-serif" }}>
            {info.name}
          </h2>
          <p className="text-muted-foreground mb-8">
            This feature requires the <span className="font-semibold text-amber-600">{info.tier}</span> plan or higher.
            Upgrade your subscription to unlock it.
          </p>
          <div className="flex flex-col gap-3">
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white gap-2"
              onClick={() => navigate("/settings?tab=billing")}
              data-testid="upgrade-btn"
            >
              <Crown className="h-4 w-4" />
              View Plans & Upgrade
            </Button>
            <Button variant="outline" onClick={() => setPreviewing(true)} className="gap-2" data-testid="preview-feature-btn">
              <Eye className="h-4 w-4" /> Preview This Feature
            </Button>
            <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Go Back
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
